import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, FileText, Building, Euro, Clock, AlertTriangle,
  TrendingUp, Calendar, CheckCircle, Plus, ArrowRight,
  HardHat, Hammer, Receipt, Mail
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

import { API_URL } from '../../../config/api';

const StatCard = ({ icon: Icon, label, value, subValue, color, trend, link }) => (
  <Link to={link || '#'}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend && (
          <span className={`text-xs px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold text-rm-text">{value}</p>
        <p className="text-sm text-rm-text-secondary mt-1">{label}</p>
        {subValue && (
          <p className="text-xs text-slate-400 mt-1">{subValue}</p>
        )}
      </div>
    </motion.div>
  </Link>
);

const QuickAction = ({ icon: Icon, label, description, link, color }) => (
  <Link to={link}>
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-all cursor-pointer flex items-center gap-4"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1">
        <p className="font-medium text-rm-text">{label}</p>
        <p className="text-xs text-rm-text-secondary">{description}</p>
      </div>
      <ArrowRight className="w-5 h-5 text-slate-400" />
    </motion.div>
  </Link>
);

export default function CRMDashboard() {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [recentProspects, setRecentProspects] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/batiment/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        setRecentProspects(data.recent_prospects || []);
        setRecentQuotes(data.recent_quotes || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Mon Cockpit</h1>
          <p className="text-rm-text-secondary">Vue d'ensemble de ton activité</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/espace-client/crm-batiment/prospects">
              <Users className="w-4 h-4 mr-2" />
              Prospects
            </Link>
          </Button>
          <Button asChild className="bg-rm-primary hover:bg-rm-primary/90">
            <Link to="/espace-client/crm-batiment/prospects?action=new">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau prospect
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Nouveaux prospects"
          value={stats?.prospects_new || 0}
          subValue={`${stats?.prospects_total || 0} au total`}
          color="bg-blue-500"
          link="/espace-client/crm-batiment/prospects"
        />
        <StatCard
          icon={FileText}
          label="Devis en attente"
          value={stats?.quotes_pending || 0}
          subValue={`${stats?.quotes_accepted || 0} acceptés`}
          color="bg-amber-500"
          link="/espace-client/crm-batiment/devis"
        />
        <StatCard
          icon={HardHat}
          label="Chantiers en cours"
          value={stats?.chantiers_in_progress || 0}
          subValue={`${stats?.chantiers_to_plan || 0} à planifier`}
          color="bg-purple-500"
          link="/espace-client/crm-batiment/chantiers"
        />
        <StatCard
          icon={Euro}
          label="CA estimé"
          value={formatCurrency(stats?.estimated_revenue)}
          subValue={`${formatCurrency(stats?.actual_revenue)} encaissé`}
          color="bg-green-500"
          link="/espace-client/crm-batiment/factures"
        />
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Receipt}
          label="Factures en attente"
          value={stats?.invoices_pending || 0}
          color="bg-orange-500"
          link="/espace-client/crm-batiment/factures"
        />
        <StatCard
          icon={AlertTriangle}
          label="Factures en retard"
          value={stats?.invoices_overdue || 0}
          color="bg-red-500"
          link="/espace-client/crm-batiment/factures?status=overdue"
        />
        <StatCard
          icon={FileText}
          label="Devis envoyés"
          value={stats?.quotes_sent || 0}
          color="bg-pink-500"
          link="/espace-client/crm-batiment/devis?status=sent"
        />
        <StatCard
          icon={CheckCircle}
          label="Devis acceptés"
          value={stats?.quotes_accepted || 0}
          color="bg-emerald-500"
          link="/espace-client/crm-batiment/devis?status=accepted"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <QuickAction
          icon={Users}
          label="Pipeline commerciale"
          description="Gérez tes opportunités"
          link="/espace-client/crm-batiment/pipeline"
          color="bg-blue-500"
        />
        <QuickAction
          icon={Hammer}
          label="Pipeline chantiers"
          description="Suis tes travaux"
          link="/espace-client/crm-batiment/chantiers"
          color="bg-purple-500"
        />
        <QuickAction
          icon={Calendar}
          label="Planification"
          description="Organisez tes équipes"
          link="/espace-client/crm-batiment/planning"
          color="bg-amber-500"
        />
        <QuickAction
          icon={FileText}
          label="Créer un devis"
          description="Nouveau devis rapide"
          link="/espace-client/crm-batiment/devis?action=new"
          color="bg-green-500"
        />
        <QuickAction
          icon={Building}
          label="Mes clients"
          description="Base de données clients"
          link="/espace-client/crm-batiment/clients"
          color="bg-indigo-500"
        />
        <QuickAction
          icon={TrendingUp}
          label="Rentabilité"
          description="Analyse tes marges"
          link="/espace-client/crm-batiment/rentabilite"
          color="bg-emerald-500"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Prospects */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-rm-text">Derniers prospects</h3>
            <Link to="/espace-client/crm-batiment/prospects" className="text-sm text-rm-primary hover:underline">
              Voir tous
            </Link>
          </div>
          {recentProspects.length === 0 ? (
            <p className="text-center py-8 text-rm-text-secondary">Aucun prospect récent</p>
          ) : (
            <div className="space-y-3">
              {recentProspects.map((prospect) => (
                <Link
                  key={prospect.id}
                  to={`/espace-client/crm-batiment/prospects/${prospect.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-rm-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-rm-text truncate">{prospect.name}</p>
                    <p className="text-xs text-rm-text-secondary">
                      {prospect.chantier_type || 'Non défini'} • {formatCurrency(prospect.estimated_value)}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    prospect.status === 'new' ? 'bg-blue-100 text-blue-600' :
                    prospect.status === 'contacted' ? 'bg-purple-100 text-purple-600' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {prospect.status === 'new' ? 'Nouveau' :
                     prospect.status === 'contacted' ? 'Contacté' :
                     prospect.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Quotes */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-rm-text">Derniers devis</h3>
            <Link to="/espace-client/crm-batiment/devis" className="text-sm text-rm-primary hover:underline">
              Voir tous
            </Link>
          </div>
          {recentQuotes.length === 0 ? (
            <p className="text-center py-8 text-rm-text-secondary">Aucun devis récent</p>
          ) : (
            <div className="space-y-3">
              {recentQuotes.map((quote) => (
                <Link
                  key={quote.id}
                  to={`/espace-client/crm-batiment/devis/${quote.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-rm-text truncate">{quote.quote_number}</p>
                    <p className="text-xs text-rm-text-secondary truncate">
                      {quote.client_name} • {formatCurrency(quote.total)}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    quote.status === 'draft' ? 'bg-slate-100 text-slate-600' :
                    quote.status === 'sent' ? 'bg-blue-100 text-blue-600' :
                    quote.status === 'accepted' ? 'bg-green-100 text-green-600' :
                    'bg-red-100 text-red-600'
                  }`}>
                    {quote.status === 'draft' ? 'Brouillon' :
                     quote.status === 'sent' ? 'Envoyé' :
                     quote.status === 'accepted' ? 'Accepté' :
                     quote.status === 'refused' ? 'Refusé' : quote.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
